import { produce } from "immer";
import { z } from "zod";
import { makeGetRequest } from "../../utils";

// Define the options type
type GetTelephoneReportOptions = {
	from: string;
	to: string;
	page_size?: number;
	next_page_token?: string;
	type?: 1 | 2 | 3;
	query_date_type?:
	| "start_time"
	| "end_time"
	| "session_start_time"
	| "session_end_time";
};

// Define the response type
const TelephonyUsageSchema = z.object({
	session_id: z.string(),
	phone_number: z.string(),
	signaled_number: z.string(),
	start_time: z.string(),
	end_time: z.string(),
	duration: z.number(),
	total: z.number(),
	country_name: z.string(),
	call_in_number: z.string(),
	type: z.string(),
	rate: z.number(),
});

const GetTelephoneReportResponseSchema = z
	.object({
		page_size: z.number(),
		next_page_token: z.string(),
		from: z.string(),
		to: z.string(),
		telephony_usage: z.array(TelephonyUsageSchema),
	})
	.deepPartial();

type GetTelephoneReportResponse = z.infer<
	typeof GetTelephoneReportResponseSchema
>;

// Define the function
const getTelephoneReport = async (
	token: string,
	options: GetTelephoneReportOptions,
): Promise<GetTelephoneReportResponse> => {
	// Make the request
	const res = await makeGetRequest({
		token,
		pathList: ["v2", "videosdk", "report", "telephone"],
		options: produce(options, (draft) => {
			if (!draft.type) {
				draft.type = 1;
			}
			if (!draft.query_date_type) {
				draft.query_date_type = "start_time";
			}
			if (!draft.page_size) {
				draft.page_size = 30;
			}
		}),
	});
	// Parse the response
	return GetTelephoneReportResponseSchema.parse(res);
};

export const curriedGetTelephoneReport = (token: string) => (
	options: GetTelephoneReportOptions,
) => getTelephoneReport(token, options);